import { styled } from 'linaria/react';
import { cx } from 'linaria';
import React from 'react';
import { theme } from '../../../Theme';
import { elementPressAnimation } from '../../../ui/animations/elementPressAnimation';
import { useLightOrDark } from '../../../../utils/useLightOrDark';

export const checkSvgDataUri = stroke => {
  const safeStroke = stroke || '#fff';
  const svg = `<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 6.5L6.0 11.0L14.5 2.0" stroke="${safeStroke}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  return `url('data:image/svg+xml,${encodeURIComponent(svg)}')`;
};

const DEFAULT_MULTICOLOR_GRADIENT =
  'conic-gradient(#f5c400 0 25%, #6a4a2b 25% 50%, #f28bb0 50% 75%, #f6efe3 75% 100%)';

const getGradientFromColors = colors => {
  if (!Array.isArray(colors) || colors.length === 0) return 'none';
  if (colors.length === 1) return colors[0];

  const sharpColorStops = colors
    .map((color, idx, arr) => {
      const segmentSize = 100 / arr.length;
      const startPct = idx * segmentSize;
      const endPct = (idx + 1) * segmentSize;
      return `${color} ${startPct}% ${endPct}%`;
    })
    .join(', ');

  return `conic-gradient(${sharpColorStops})`;
};

const getFacetColors = value => {
  if (Array.isArray(value?.colors) && value.colors.length > 0) {
    return value.colors;
  }
  if (Array.isArray(value?.color) && value.color.length > 0) {
    return value.color;
  }
  if (typeof value?.color === 'string' && value.color.includes(',')) {
    return value.color
      .split(',')
      .map(color => color.trim())
      .filter(Boolean);
  }
  if (value?.color) {
    return [value.color];
  }
  return null;
};

const getFacetColorBackground = value => {
  const colors = getFacetColors(value);
  if (!colors?.length) {
    return DEFAULT_MULTICOLOR_GRADIENT;
  }
  return getGradientFromColors(colors) || DEFAULT_MULTICOLOR_GRADIENT;
};

export const ColorFacet = ({ clickCallback, ...props }) => {
  return (
    <ColorFacetGrid className="elevate-color-facet">
      {props?.values?.map((value, index) => {
        if (value.count === 0) {
          return null;
        }
        return (
          <ColorFacetBlock
            key={`facet-${index}`}
            value={value}
            clickCallback={clickCallback}
            typeObject={props}
            {...props}
          />
        );
      })}
    </ColorFacetGrid>
  );
};

const ColorFacetGrid = styled('ul')`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px 28px;
  padding-top: 6px;
`;

const ColorFacetBlockWrapper = styled('li')`
  label {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    cursor: pointer;
    user-select: none;
    text-align: center;
    font-size: 16px;
    line-height: 20px;
    color: ${theme.colors.primary};
  }

  &.checked label {
    color: ${theme.colors.accent};
  }

  .color-orb {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 54px;
    height: 54px;
    border-radius: 50%;
    border: 1px solid ${theme.colors.lightgrey};
    background: ${props => props.orbBackground || DEFAULT_MULTICOLOR_GRADIENT};
  }
  &.checked {
    label {
      color: #0d3923;
      font-weight: 600;
    }
    .color-orb {
      outline: 2px solid #000;
      outline-offset: 1px;

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background-image: ${props => checkSvgDataUri(props.svgcolor)};
        background-repeat: no-repeat;
        background-position: center;
        background-size: 22px 22px;
      }
    }
  }

  input[type='checkbox'] {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 1px;
    height: 1px;
  }
`;

const ColorFacetBlock = ({ value, typeObject, clickCallback }) => {
  const { multiColors } = useLightOrDark();
  const facetColors = getFacetColors(value) || [];
  const orbBackground = getFacetColorBackground(value);
  const svgColor = multiColors(facetColors.length ? facetColors : ['#ccc']);

  return (
    <ColorFacetBlockWrapper
      orbBackground={orbBackground}
      svgcolor={svgColor === 'light' ? '#000' : '#fff'}
      className={cx(value.selected && 'checked')}
    >
      <label>
        <div
          className={cx('color-orb', elementPressAnimation)}
          aria-hidden="true"
        />
        <input
          type="checkbox"
          checked={value.selected}
          onChange={() => clickCallback(value, typeObject)}
        />
        {value.label}
      </label>
    </ColorFacetBlockWrapper>
  );
};
